const trim = (value) =>
  typeof value === 'string'
    ? value.trim()
    : value ?? '';

const getLocalToday = () => {
  const today = new Date();

  const offset =
    today.getTimezoneOffset() * 60 * 1000;

  return new Date(
    today.getTime() - offset,
  )
    .toISOString()
    .slice(0, 10);
};

const getId = (value) => {
  if (!value) {
    return '';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number'
  ) {
    return String(value);
  }

  return String(
    value.id ||
      value.value ||
      '',
  );
};

const createAddress = (
  addressType,
  address = {},
) => ({
  id: address?.id || '',
  address_type: addressType,
  address_line1:
    address?.address_line1 || '',
  address_line2:
    address?.address_line2 || '',
  city: address?.city || '',
  state: address?.state || '',
  country:
    address?.country ||
    (addressType === 'CURRENT'
      ? 'India'
      : ''),
  pincode: address?.pincode || '',
});

export const createEmptyStudentValues = () => ({
  enrolment_no: '',
  admission_no: '',

  first_name: '',
  middle_name: '',
  last_name: '',

  gender: '',
  date_of_birth: '',
  blood_group: '',
  nationality: 'Indian',
  religion: '',
  caste_category: '',
  aadhaar_no: '',

  admission_date: getLocalToday(),
  status: 'ACTIVE',

  academic_profile: {
    academic_year: '',
    class_level: '',
    section: '',
    roll_no: '',
    status: 'ACTIVE',
  },

  parent_detail: {
    father_name: '',
    father_phone: '',
    father_email: '',
    father_occupation: '',

    mother_name: '',
    mother_phone: '',
    mother_email: '',
    mother_occupation: '',

    primary_contact_name: '',
    primary_contact_phone: '',
  },

  addresses: [
    createAddress('CURRENT'),
    createAddress('PERMANENT'),
  ],

  medical_detail: {
    allergies: '',
    medical_conditions: '',
    regular_medication: '',
    doctor_name: '',
    doctor_phone: '',
    emergency_instructions: '',
  },

  previous_school_detail: {
    previous_school_name: '',
    previous_class: '',
    transfer_certificate_no: '',
    reason_for_leaving: '',
    last_attended_date: '',
  },

  docs: {
    birth_certificate: null,
    transfer_certificate: null,
    id_proof: null,
    passport_photo: null,
  },
});

const getCurrentAcademicProfile = (
  student,
) => {
  const profiles = Array.isArray(
    student?.academic_profiles,
  )
    ? student.academic_profiles
    : [];

  const activeProfile = profiles.find(
    (profile) =>
      profile?.status === 'ACTIVE',
  );

  return (
    activeProfile ||
    student?.current_academic ||
    profiles[0] ||
    {}
  );
};

export const mapStudentDetailToForm = (
  student,
) => {
  const emptyValues =
    createEmptyStudentValues();

  const academicProfile =
    getCurrentAcademicProfile(student);

  const addresses = Array.isArray(
    student?.addresses,
  )
    ? student.addresses
    : [];

  const currentAddress = addresses.find(
    (address) =>
      address?.address_type === 'CURRENT',
  );

  const permanentAddress = addresses.find(
    (address) =>
      address?.address_type ===
      'PERMANENT',
  );

  const parent =
    student?.parent_detail || {};

  const medical =
    student?.medical_detail || {};

  const previousSchool =
    student?.previous_school_detail || {};

  return {
    ...emptyValues,

    enrolment_no:
      student?.enrolment_no || '',
    admission_no:
      student?.admission_no || '',

    first_name:
      student?.first_name || '',
    middle_name:
      student?.middle_name || '',
    last_name:
      student?.last_name || '',

    gender: student?.gender || '',
    date_of_birth:
      student?.date_of_birth || '',
    blood_group:
      student?.blood_group || '',
    nationality:
      student?.nationality || 'Indian',
    religion:
      student?.religion || '',
    caste_category:
      student?.caste_category || '',
    aadhaar_no:
      student?.aadhaar_no || '',

    admission_date:
      student?.admission_date || '',
    status:
      student?.status || 'ACTIVE',

    academic_profile: {
      academic_year: getId(
        academicProfile?.academic_year ||
          academicProfile
            ?.academic_year_id,
      ),
      class_level: getId(
        academicProfile?.class_level ||
          academicProfile
            ?.class_level_id,
      ),
      section: getId(
        academicProfile?.section ||
          academicProfile?.section_id,
      ),
      roll_no:
        academicProfile?.roll_no || '',
      status:
        academicProfile?.status ||
        'ACTIVE',
    },

    parent_detail: {
      father_name:
        parent.father_name || '',
      father_phone:
        parent.father_phone || '',
      father_email:
        parent.father_email || '',
      father_occupation:
        parent.father_occupation || '',

      mother_name:
        parent.mother_name || '',
      mother_phone:
        parent.mother_phone || '',
      mother_email:
        parent.mother_email || '',
      mother_occupation:
        parent.mother_occupation || '',

      primary_contact_name:
        parent.primary_contact_name || '',
      primary_contact_phone:
        parent.primary_contact_phone || '',
    },

    addresses: [
      createAddress(
        'CURRENT',
        currentAddress,
      ),
      createAddress(
        'PERMANENT',
        permanentAddress,
      ),
    ],

    medical_detail: {
      allergies:
        medical.allergies || '',
      medical_conditions:
        medical.medical_conditions || '',
      regular_medication:
        medical.regular_medication || '',
      doctor_name:
        medical.doctor_name || '',
      doctor_phone:
        medical.doctor_phone || '',
      emergency_instructions:
        medical.emergency_instructions ||
        '',
    },

    previous_school_detail: {
      previous_school_name:
        previousSchool
          .previous_school_name || '',
      previous_class:
        previousSchool.previous_class ||
        '',
      transfer_certificate_no:
        previousSchool
          .transfer_certificate_no || '',
      reason_for_leaving:
        previousSchool
          .reason_for_leaving || '',
      last_attended_date:
        previousSchool
          .last_attended_date || '',
    },

    docs: emptyValues.docs,
  };
};

const cleanAddress = (address) => ({
  address_type:
    address.address_type,
  address_line1: trim(
    address.address_line1,
  ),
  address_line2: trim(
    address.address_line2,
  ),
  city: trim(address.city),
  state: trim(address.state),
  country: trim(address.country),
  pincode: trim(address.pincode),
});

const addressHasValues = (address) =>
  Boolean(
    address?.id ||
      trim(address?.address_line1) ||
      trim(address?.address_line2) ||
      trim(address?.city) ||
      trim(address?.state) ||
      trim(address?.country) ||
      trim(address?.pincode),
  );

export const buildStudentPayload = (
  values,
) => {
  const currentAddress =
    values.addresses?.find(
      (address) =>
        address?.address_type ===
        'CURRENT',
    ) ||
    values.addresses?.[0] ||
    {};

  const permanentAddress =
    values.addresses?.find(
      (address) =>
        address?.address_type ===
        'PERMANENT',
    ) ||
    values.addresses?.[1] ||
    {};

  const addresses = [
    cleanAddress({
      ...currentAddress,
      address_type: 'CURRENT',
    }),
  ];

  if (
    addressHasValues(permanentAddress)
  ) {
    addresses.push(
      cleanAddress({
        ...permanentAddress,
        address_type: 'PERMANENT',
      }),
    );
  }

  return {
    enrolment_no: trim(
      values.enrolment_no,
    ),

    admission_no:
      trim(values.admission_no) ||
      null,

    first_name: trim(
      values.first_name,
    ),
    middle_name: trim(
      values.middle_name,
    ),
    last_name: trim(
      values.last_name,
    ),

    gender: values.gender,
    date_of_birth:
      values.date_of_birth,

    blood_group: trim(
      values.blood_group,
    ),
    nationality: trim(
      values.nationality,
    ),
    religion: trim(
      values.religion,
    ),
    caste_category: trim(
      values.caste_category,
    ),
    aadhaar_no: String(
      values.aadhaar_no || '',
    ).replace(/\s/g, ''),

    admission_date:
      values.admission_date,
    status:
      values.status || 'ACTIVE',

    academic_profile: {
      academic_year:
        values.academic_profile
          .academic_year,

      class_level:
        values.academic_profile
          .class_level,

      section:
        values.academic_profile
          .section,

      roll_no: trim(
        values.academic_profile
          .roll_no,
      ),

      status:
        values.academic_profile
          .status || 'ACTIVE',
    },

    parent_detail: {
      father_name: trim(
        values.parent_detail
          .father_name,
      ),

      father_phone: trim(
        values.parent_detail
          .father_phone,
      ),

      father_email: trim(
        values.parent_detail
          .father_email,
      ).toLowerCase(),

      father_occupation: trim(
        values.parent_detail
          .father_occupation,
      ),

      mother_name: trim(
        values.parent_detail
          .mother_name,
      ),

      mother_phone: trim(
        values.parent_detail
          .mother_phone,
      ),

      mother_email: trim(
        values.parent_detail
          .mother_email,
      ).toLowerCase(),

      mother_occupation: trim(
        values.parent_detail
          .mother_occupation,
      ),

      primary_contact_name: trim(
        values.parent_detail
          .primary_contact_name,
      ),

      primary_contact_phone: trim(
        values.parent_detail
          .primary_contact_phone,
      ),
    },

    addresses,

    medical_detail: {
      allergies: trim(
        values.medical_detail
          .allergies,
      ),

      medical_conditions: trim(
        values.medical_detail
          .medical_conditions,
      ),

      regular_medication: trim(
        values.medical_detail
          .regular_medication,
      ),

      doctor_name: trim(
        values.medical_detail
          .doctor_name,
      ),

      doctor_phone: trim(
        values.medical_detail
          .doctor_phone,
      ),

      emergency_instructions: trim(
        values.medical_detail
          .emergency_instructions,
      ),
    },

    previous_school_detail: {
      previous_school_name: trim(
        values.previous_school_detail
          .previous_school_name,
      ),

      previous_class: trim(
        values.previous_school_detail
          .previous_class,
      ),

      transfer_certificate_no: trim(
        values.previous_school_detail
          .transfer_certificate_no,
      ),

      reason_for_leaving: trim(
        values.previous_school_detail
          .reason_for_leaving,
      ),

      last_attended_date:
        values.previous_school_detail
          .last_attended_date ||
        null,
    },
  };
};

export default buildStudentPayload;